import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import find from "lodash/find"
import { useState, useEffect } from "react"
import { screenReaderSpeak } from "utils-lib/accessibility/screenReader"
import { ReactSVG } from "react-svg"
import { Configure, Index, useSearchBox } from "react-instantsearch"
import InputAdornment from "component-lib/Base/components/InputAdornment"
import TextField from "component-lib/Base/components/TextField/TextField"
import { makeStyles } from "tss-react/mui"
import getContentUrl from "utils-lib/getContentUrl"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import { withLocalization } from "hoc-lib/localization"
import { getWindow } from "utils-lib/getWindow"
import { useAutocomplete } from "../utils/useAutocomplete"
import Queries from "../components/Queries/Queries"
import Suggestions from "../components/Suggestions/Suggestions"
import Results from "../components/Results/Results"
import { Link } from "../../Base"

const ssrWindow = getWindow()

let timer

export const strings = {
  en_CA: {
    RESULTS: `Results`,
  },
  fr_CA: {
    RESULTS: `Résultats`,
  },
}

const useStyles = makeStyles()(() => ({
  navInputText: {
    margin: 0,
    textIndent: 6,
    padding: 0,
    input: {
      padding: `6px 0 7px`,
    },
    " & .MuiInputAdornment-root": {
      ".notranslate": {
        display: "none",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: `none`,
      padding: `0 !important`,
      "& fieldset > legend": {
        display: "none !important",
      },
    },
  },
  svg: {
    "& > svg": {
      height: 25,
      width: 25,
    },
    "& ellipse, line": {
      stroke: `#1D890A`,
    },
  },
  inputUnderline: {
    background: `#1D890A`,
    height: 2,
  },
  allResults: {
    border: `1px solid #D2E7CE !important`,
    borderRadius: `5px !important`,
  },
}))

export const GlobalMobileSearch = ({
  indices = [],
  refine = () => {},
  resultsIndex,
  error = null,
  errorMessageDescription,
  errorMessageTitle,
  failureDescription,
  failureTitle,
  hits,
  isSupportSearch,
  language,
  numCharacters,
  numHighlightedResults,
  numResults,
  numberQueries,
  queryIndex,
  filterType,
  resultsText,
  search,
  searchPlaceholder,
  seeAllResultsText,
  setSearch,
  strings,
  suggestedText,
  suggestionIndex,
  searchDelay,
  isNewDashboard = false,
  onClose = () => {},
}) => {
  const [isFocused, setIsFocused] = useState(false)

  const { classes } = useStyles()

  const allSearchResults = get(
    find(indices, ({ indexId }) => `${resultsIndex}-results` === indexId),
    `results`,
  )

  const languageRoute = getLanguageRoute(language)

  let queries = find(hits, (item) => item.index === queryIndex)

  if (queries) {
    queries = queries.hits
  } else {
    queries = []
  }

  const suggestionsResults =
    allSearchResults && allSearchResults[suggestionIndex]
  let suggestionsCount = 0

  if (suggestionsResults) {
    suggestionsCount = suggestionsResults.hits.length
  }

  const totalResults = allSearchResults
    ? get(allSearchResults, `nbHits`, 0) ||
      get(allSearchResults[resultsIndex], `nbHits`, 0)
    : 0

  function renderSuggested(hideDescription) {
    if (isSupportSearch) {
      return null
    }

    return (
      <Index
        indexName={suggestionIndex}
        indexId={`${suggestionIndex}-suggested`}
      >
        <Configure
          analyticsTags={["mobile-global-suggested"]}
          hitsPerPage={numHighlightedResults}
          filters={`tags:'${filterType}'`}
          clickAnalytics
        />
        <Suggestions
          className="SiteSearch__result-mobile"
          indexName={`${suggestionIndex}-suggested`}
          hideDescription={hideDescription}
        />
      </Index>
    )
  }

  function renderResults() {
    return (
      <Index indexName={resultsIndex} indexId={`${resultsIndex}-results`}>
        <Configure
          analyticsTags={["mobile-global-results"]}
          hitsPerPage={numResults}
          clickAnalytics
          filters={isSupportSearch ? "searchType:support" : ""}
        />
        <Results
          className="SiteSearch__result-mobile"
          indexName={`${resultsIndex}-results`}
          hideDescription
        />
      </Index>
    )
  }

  const isPreSearch = search.length < numCharacters

  useEffect(() => {
    screenReaderSpeak(
      isPreSearch
        ? suggestedText
        : `${totalResults} ${resultsText || strings.RESULTS}`,
      "assertive",
    )
  }, [isPreSearch, totalResults, resultsText, strings.RESULTS, search])

  return (
    <div
      className={`maax-font-weight-regular ${classes.inputContainer}`}
      onClick={(e) => {
        const link = e.target.closest("a[href]")
        if (link) {
          e.preventDefault()
          onClose()
          setTimeout(() => {
            ssrWindow.location.href = link.href
          }, 0)
        }
      }}
    >
      <TextField
        id="full-width-text-field-header"
        className={`SiteSearch__searchbox-mobile p-2 ${classes.navInputText}`}
        placeholder={isSupportSearch ? "Search WM Support" : searchPlaceholder}
        value={search}
        onFocus={() => {
          setIsFocused(true)
        }}
        autoFocus={isNewDashboard}
        onBlur={() => {
          setIsFocused(false)
        }}
        onChange={(e) => {
          const { value } = e.target

          setSearch(value)

          const supportAnalytics = ssrWindow._dl.supportDataLayer9_0
          if (!supportAnalytics) {
            ssrWindow._dl.supportDataLayer9_0 = {
              supportPageSearchResults: value,
            }
          } else {
            ssrWindow._dl.supportDataLayer9_0.supportPageSearchResults = value
          }

          if (value.length >= numCharacters || !value.length) {
            clearTimeout(timer)
            timer = setTimeout(() => refine(value), searchDelay)
          }
        }}
        fullWidth
        margin="normal"
        InputProps={{
          disableUnderline: true,
          startAdornment: (
            <InputAdornment position="start">
              <ReactSVG
                wrapper="span"
                className={classes.svg}
                src={getContentUrl("/content/dam/wm/icons/search-icon.svg")}
              />
            </InputAdornment>
          ),
          "aria-describedby": "search-results",
          inputProps: {
            "aria-label": "Search box input",
          },
        }}
      />
      {isFocused && (
        <div className={`mx-2 position-relative ${classes.inputUnderline}`} />
      )}
      <div className={classes.resultsContainer}>
        <div>
          <div
            className="font-xs py-1 px-4"
            style={{
              background: `#f4f4f4`,
              textIndent: 10,
            }}
            aria-live="polite"
            aria-atomic="true"
            role="status"
            id="search-results-mobile"
          >
            {isPreSearch
              ? suggestedText
              : `${totalResults} ${resultsText || strings.RESULTS}`}
          </div>
          {!isPreSearch && totalResults === 0 && suggestionsCount === 0 && (
            <div className="mt-3 mx-3">
              {!isEmpty(error) && (
                <div className="mx-3 wm-grey-light">
                  <div className="maax-font-weight-bold">
                    {error?.name ? failureTitle : errorMessageTitle}
                  </div>
                  <div className="font-sm">
                    {error?.name ? failureDescription : errorMessageDescription}
                  </div>
                </div>
              )}
            </div>
          )}
          <>
            <Index indexName={queryIndex}>
              <Configure
                analyticsTags={["mobile-global-query"]}
                hitsPerPage={numberQueries}
                clickAnalytics
              />
              <Queries
                indexName={queryIndex}
                languageRoute={languageRoute}
                isPreSearch={isPreSearch}
              />
            </Index>
            {renderSuggested(true)}
            {renderResults()}
            {Boolean(
              !isSupportSearch &&
                !isPreSearch &&
                (totalResults || suggestionsCount),
            ) && (
              <div className="d-flex justify-content-center mb-3 mt-3">
                <Link
                  className={`maax-font-weight-bold py-2 px-3 font-sm ${classes.allResults}`}
                  href={`${languageRoute}/search?q=${encodeURIComponent(
                    search,
                  )}`}
                  isExternalLink
                >
                  {seeAllResultsText}
                </Link>
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// GlobalMobileSearch.propTypes = {
//   indices: PropTypes.array,
//   refine: PropTypes.func,
//   resultsIndex: PropTypes.string.isRequired,
//   error: PropTypes.object,
//   errorMessageDescription: PropTypes.string.isRequired,
//   errorMessageTitle: PropTypes.string.isRequired,
//   failureDescription: PropTypes.string.isRequired,
//   failureTitle: PropTypes.string.isRequired,
//   hits: PropTypes.array.isRequired,
//   isSupportSearch: PropTypes.bool.isRequired,
//   language: PropTypes.string.isRequired,
//   numCharacters: PropTypes.number.isRequired,
//   numHighlightedResults: PropTypes.number.isRequired,
//   numResults: PropTypes.number.isRequired,
//   numberQueries: PropTypes.number.isRequired,
//   queryIndex: PropTypes.string.isRequired,
//   filterType: PropTypes.string.isRequired,
//   resultsText: PropTypes.string.isRequired,
//   search: PropTypes.string.isRequired,
//   searchPlaceholder: PropTypes.string.isRequired,
//   seeAllResultsText: PropTypes.string.isRequired,
//   setSearch: PropTypes.func.isRequired,
//   strings: PropTypes.object.isRequired,
//   suggestedText: PropTypes.string.isRequired,
//   suggestionIndex: PropTypes.string.isRequired,
//   searchDelay: PropTypes.number.isRequired,
//   isNewDashboard: PropTypes.bool,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// GlobalMobileSearch.defaultProps = {
//   isNewDashboard: false,
//   indices: [],
//   refine: () => {},
//   error: null,
// }

const GlobalMobileSearchContainer = (props) => {
  const stateResults = useSearchBox(props)
  const autoCompleteResults = useAutocomplete({ ...props, ...stateResults })

  return (
    <GlobalMobileSearch {...props} {...stateResults} {...autoCompleteResults} />
  )
}

export default withLocalization(strings)(GlobalMobileSearchContainer)
